import React, { useEffect, useState } from 'react';
import { Col, FormInput, FormRadioGroup, Heading, InPageAlert, Row } from '@snsw/react-component-library';
import PropTypes from 'prop-types';
import {
  isEmpty,
  isResidentialAddress,
  isStreetAddressValid,
  isValidEmail,
  validateExt,
} from 'shared/utils/Validation';
import { AddressWithDuplicate } from 'shared/components/address/AddressWithDuplicate';
import { OrganisationSearchAutoComplete } from 'shared/components/organisationSearch/OrganisationSearchAutoComplete';
import { ItemRegistrationHolderPage } from 'plantItemNew/constants/itemRegistrationHolderPage';
import _ from 'lodash';
import { NewContactNumber } from 'shared/components/contactNumber/NewContactNumber';
import { PhoneNumber } from 'shared/utils/PhoneNumber';
import styles from '../../Page.module.scss';
import { PlantItemNewOngoingContactTypes as OngoingContactTypes } from 'plantItemNew/constants/plantItemConstants';

const OrganisationHolderFields = React.forwardRef((props, ref) => {
  const { value: savedHolderDetails = {}, onErrorChange = () => {} } = props;
  const {
    emailAddress: savedEmailAddress,
    organisation: {
      abn = '',
      organisationName = '',
      selectedBusinessName: savedSelectedBusinessName = '',
      acn = '',
    } = {},
    isSameAddress: savedIsSameAddress = true,
    address: savedBusinessAddress = {},
    mailingAddress: savedMailingAddress = {},
    phoneNumber: savedPhoneNumber,
    ongoingContact: savedOngoingContact,
  } = savedHolderDetails;

  const [emailAddress, setEmailAddress] = useState(savedEmailAddress);
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber);

  const [organisation, setOrganisation] = useState({
    abn,
    acn,
    name: organisationName,
  });
  const [selectedBusinessName, setSelectedBusinessName] = useState(savedSelectedBusinessName);

  const [businessAddressValid, setBusinessAddressValid] = useState(false);
  const [mailingAddressValid, setMailingAddressValid] = useState(false);
  const [isSameAddress, setIsSameAddress] = useState(savedIsSameAddress);
  const [isAddressLoading, setAddressLoading] = useState(false);
  const [businessAddress, setBusinessAddress] = useState(_.cloneDeep(savedBusinessAddress));
  const [mailingAddress, setMailingAddress] = useState(_.cloneDeep(savedMailingAddress));

  const [ongoingContactType, setOngoingContactType] = useState(savedOngoingContact?.contactType || '');
  const [ongoingContactEmailAddress, setOngoingContactEmailAddress] = useState(savedOngoingContact?.emailAddress || '');
  const [ongoingContactPhoneNumber, setOngoingContactPhoneNumber] = useState(savedOngoingContact?.phoneNumber || {});

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);

  const ongoingContactTypeOptions = [
    { label: OngoingContactTypes.me.label, value: OngoingContactTypes.me.value },
    { label: OngoingContactTypes.someoneElse.label, value: OngoingContactTypes.someoneElse.value },
  ];

  useEffect(() => {
    if (props.onChange) {
      let mydetails = {
        organisation: {
          abn: organisation?.abn,
          organisationName: organisation?.name,
          selectedBusinessName,
          acn: organisation?.acn,
        },
        emailAddress,
        address: businessAddress,
        isSameAddress,
        mailingAddress,
        phoneNumber,
        ongoingContact: {
          contactType: ongoingContactType,
          emailAddress: ongoingContactEmailAddress,
          phoneNumber: ongoingContactPhoneNumber,
        },
      };
      props.onChange(mydetails);
    }

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    organisation,
    selectedBusinessName,
    businessAddressValid,
    mailingAddressValid,
    businessAddress,
    isSameAddress,
    mailingAddress,
    emailAddress,
    phoneNumber,
    ongoingContactType,
    ongoingContactEmailAddress,
    ongoingContactPhoneNumber,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const onChangeEmail = (e, { value }) => {
    setEmailAddress(value);
  };

  const validators = [
    {
      fieldName: 'organisation',
      fieldValue: organisation,
      isValid: (v) => !isEmpty(v?.abn) || !isEmpty(v?.acn),
      errorMessage: () => 'Organisation is required.',
    },
    {
      fieldName: 'organisationAddresses',
      fieldValue: null,
      isValid: () => businessAddressValid && mailingAddressValid,
      errorMessage: () => 'Registered business address is required.',
    },
    {
      fieldName: 'organisationAddresses',
      fieldValue: null,
      isValid: () => isResidentialAddress(businessAddress),
      errorMessage: () => 'Enter a valid registered office address.',
    },
    {
      fieldName: 'organisationAddresses',
      fieldValue: null,
      isValid: () => isStreetAddressValid(businessAddress, 64),
      errorMessage: () => 'The length of the street address cannot be more than 64 characters.',
    },
    {
      fieldName: 'organisationAddresses',
      fieldValue: null,
      isValid: () => isSameAddress || isStreetAddressValid(mailingAddress, 64),
      errorMessage: () => 'The length of the street address cannot be more than 64 characters.',
    },
    {
      fieldName: 'emailAddress',
      fieldValue: emailAddress,
      isValid: (v) => isValidEmail(v),
      errorMessage: (v) => (isEmpty(v) ? 'Email address is required.' : 'Enter a valid email address.'),
    },
    {
      fieldName: 'phoneNumber',
      fieldValue: phoneNumber,
      isValid: (v) => !!v?.formattedNumber,
      errorMessage: (v) => (isEmpty(v?.raw?.number) ? 'Phone number is required.' : 'Enter a valid phone number.'),
    },
    {
      fieldName: 'ongoingContactType',
      fieldValue: ongoingContactType,
      isValid: (v) => ongoingContactTypeOptions.map((opt) => opt.value).includes(v),
      errorMessage: () => 'Ongoing contact type is required.',
    },
    {
      fieldName: 'ongoingContactEmailAddress',
      fieldValue: ongoingContactEmailAddress,
      isValid: (v) => ongoingContactType != OngoingContactTypes.someoneElse.value || isValidEmail(v),
      errorMessage: (v) => (isEmpty(v) ? 'Email address is required.' : 'Enter a valid email address.'),
    },
    {
      fieldName: 'ongoingContactPhoneNumber',
      fieldValue: ongoingContactPhoneNumber,
      isValid: (v) => !!v?.formattedNumber,
      errorMessage: (v) => (isEmpty(v?.raw?.number) ? 'Phone number is required.' : 'Enter a valid phone number.'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        let isAddressValid = businessAddressValid && mailingAddressValid;

        if (isCurrentComponentValid && isAddressValid) setShowErrors(false);
        else setShowErrors(true);

        return isCurrentComponentValid;
      },
    };
  }

  return (
    <>
      <Row style={{ marginTop: '3.5rem' }}>
        <Col span={9}>
          <InPageAlert variant="info" title="You must have consent before you share someone else’s personal details.">
            If you’re not the registration holder, you need to get permission to share their personal details in this
            application.
          </InPageAlert>
          <Heading level={2}>Organisation details</Heading>
          <OrganisationSearchAutoComplete
            id="organisation"
            baseURL={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}`}
            selectedOrganisation={organisation}
            onChangeOrganisation={setOrganisation}
            selectedBusinessName={selectedBusinessName}
            onChangeBusinessName={setSelectedBusinessName}
            hasError={showErrors && errors.organisation}
            errorMessage={errors.organisation?.errorMessage}
            onClear={() => {
              setOrganisation(undefined);
              setSelectedBusinessName('');
            }}
            helpMessage="Start typing to search for an ABN, ACN and business name. Please enter complete ABN/ACN for exact search result"
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col span={9}>
          <div id="organisationAddresses">
            <AddressWithDuplicate
              autoValidate
              firstAddressLabel={ItemRegistrationHolderPage.registeredBusinessAddressLabel}
              firstAddressMessage="Start typing to search"
              secondAddressLabel={ItemRegistrationHolderPage.mailingAddressLabel}
              secondAddressMessage="Start typing to search"
              baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}`}
              showErrors={{
                firstAddress: showErrors && !businessAddressValid,
                secondAddress: showErrors && !mailingAddressValid,
              }}
              onChange={(data) => {
                setIsSameAddress(data.isSameAddress);
                setBusinessAddress(data.firstAddress);
                setMailingAddress(data.secondAddress);
              }}
              onLoading={(data) => {
                setAddressLoading(!!(data.isFirstAddressValidating || data.isSecondAddressValidating));
              }}
              onValidationChange={(v) => {
                setBusinessAddressValid(v.firstAddressValid);
                setMailingAddressValid(v.secondAddressValid);
              }}
              value={{
                isSameAddress,
                firstAddress: businessAddress,
                secondAddress: mailingAddress,
              }}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={9}>
          <FormInput
            id="emailAddress"
            label={ItemRegistrationHolderPage.emailAddressLabel}
            helpMessage={ItemRegistrationHolderPage.organisationEmailAddressHelpText}
            name="emailAddress"
            type="email"
            errorMessage={errors?.emailAddress?.errorMessage}
            hasError={showErrors && errors?.emailAddress?.invalid}
            value={emailAddress}
            onChange={onChangeEmail}
            inputWidth="xl"
          />
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={9}>
          <NewContactNumber
            id="phoneNumber"
            onChange={(v) => {
              setPhoneNumber(new PhoneNumber('', v, undefined, true));
            }}
            value={phoneNumber?.raw?.number}
            label={ItemRegistrationHolderPage.phoneNumberLabel}
            helpMessage={ItemRegistrationHolderPage.phoneNumberHelpText}
            errorMessage={errors?.phoneNumber?.errorMessage}
            hasError={showErrors && errors?.phoneNumber?.invalid}
            allowNonNumeric={false}
            inputWidth={'lg'}
          />
          <Heading level={2}>Ongoing contact</Heading>
          <p>The best contact for all future communications about this registration, including renewal reminders.</p>
        </Col>
      </Row>
      <Row>
        <Col span={9}>
          <FormRadioGroup
            id="ongoingContactType"
            name="ongoingContactType"
            legend={ItemRegistrationHolderPage.ongoingContactTypeLabel}
            vertical={true}
            options={ongoingContactTypeOptions}
            onChange={setOngoingContactType}
            value={ongoingContactType}
            errorMessage={errors?.ongoingContactType?.errorMessage}
            hasError={showErrors && errors?.ongoingContactType?.invalid}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '2rem' }}>
        <Col span={9}>
          {ongoingContactType === OngoingContactTypes.someoneElse.value && (
            <FormInput
              id="ongoingContactEmailAddress"
              label={ItemRegistrationHolderPage.emailAddressLabel}
              helpMessage={ItemRegistrationHolderPage.ongoingContactEmailAddressHelpText}
              name="ongoingContactEmailAddress"
              type="email"
              errorMessage={errors?.ongoingContactEmailAddress?.errorMessage}
              hasError={showErrors && errors?.ongoingContactEmailAddress?.invalid}
              value={ongoingContactEmailAddress}
              onChange={(e, { value }) => {
                setOngoingContactEmailAddress(value);
              }}
              inputWidth="xl"
              className={styles.marginBottomOneHalf}
            />
          )}
          {ongoingContactType && (
            <NewContactNumber
              id="ongoingContactPhoneNumber"
              onChange={(v) => {
                setOngoingContactPhoneNumber(new PhoneNumber('', v, undefined, true));
              }}
              value={ongoingContactPhoneNumber?.raw?.number}
              label={ItemRegistrationHolderPage.ongoingContactPhoneNumberLabel}
              helpMessage={ItemRegistrationHolderPage.ongoingContactPhoneNumberHelpText}
              errorMessage={errors?.ongoingContactPhoneNumber?.errorMessage}
              hasError={showErrors && errors?.ongoingContactPhoneNumber?.invalid}
              allowNonNumeric={false}
              inputWidth={'lg'}
            />
          )}
        </Col>
      </Row>
    </>
  );
});
OrganisationHolderFields.displayName = 'OrganisationHolderFields';

OrganisationHolderFields.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
};

OrganisationHolderFields.defaultProps = {
  onChange: null,
  value: null,
};

export { OrganisationHolderFields };
